import { Popover, PopoverContent, PopoverTrigger } from '../../radix/popover'
import { CircleButton } from '../../../components'
import { ReactElement, useState, useImperativeHandle, forwardRef } from 'react'
import { MAX_NUMBER_OF_ATTACHMENTS, useAttachments, useIsActiveTurn } from '../../../store'
import { cn } from '../../../utils'
import { Interactive, Tooltip } from '@highlight/ui'

type AttachmentMenuType = {
  buttonClassName?: string
  icon: ReactElement
  menuClassName?: string
  onOpenChange?: (open: boolean) => void
  tooltip: string | ReactElement
  tooltipHotkey?: string
}

export type AttachmentMenuHandle = {
  close: () => void
}

export const AttachmentMenu = forwardRef<AttachmentMenuHandle, React.PropsWithChildren<AttachmentMenuType>>(
  ({ buttonClassName, children, icon, menuClassName, onOpenChange, tooltip, tooltipHotkey }, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const attachments = useAttachments()
    const isChatLoading = useIsActiveTurn()
    const isDisabled = isChatLoading || attachments.length >= MAX_NUMBER_OF_ATTACHMENTS

    useImperativeHandle(
      ref,
      () => ({
        close: () => setIsOpen(false),
      }),
      [],
    )

    const onChange = (open: boolean) => {
      if (typeof onOpenChange === 'function') {
        onOpenChange(open)
      }
      setIsOpen(open)
    }

    return (
      <Popover open={isOpen} onOpenChange={onChange}>
        <PopoverTrigger disabled={isDisabled} className={'m-0 border-none bg-transparent p-0 outline-none'}>
          <Tooltip disabled={isOpen || isChatLoading}>
            <Tooltip.Trigger delay={200} render={<div className="relative flex" />}>
              <CircleButton
                className={cn('hover:bg-light-20 h-7 w-7 opacity-100', buttonClassName)}
                disabled={isDisabled}
                onClick={(e) => {
                  e.stopPropagation()
                  setIsOpen(!isOpen)
                }}
              >
                {icon}
              </CircleButton>
            </Tooltip.Trigger>
            <Tooltip.Content>
              {isDisabled ? <span className={'text-red'}>Max attachments reached!</span> : tooltip}
              {!isDisabled && tooltipHotkey && <Tooltip.Hotkey hotkey={tooltipHotkey} />}
            </Tooltip.Content>
          </Tooltip>
        </PopoverTrigger>
        <PopoverContent
          side={'bottom'}
          sideOffset={8}
          align={'center'}
          className={cn(
            'bg-secondary-old/[97%] text-light flex w-fit flex-col overflow-hidden rounded-2xl border-none p-0',
            menuClassName,
          )}
        >
          {children}
        </PopoverContent>
      </Popover>
    )
  },
)

export const AttachmentMenuItem = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => {
    return (
      <div
        className={cn(
          'group flex min-h-10 w-full max-w-72 cursor-pointer items-center gap-3 px-4 py-2',
          'hover:text-light text-light/50 select-none text-[13px] font-normal transition-colors',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    )
  },
)
